import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { Plus, Search, Package, ChevronDown, ChevronUp, X, Database, ChevronLeft, ChevronRight, Barcode, Download, Printer } from 'lucide-react';
import { useAutosave, loadDraft, clearDraft } from '../hooks/useAutosave';
import BarcodeScanInput from '../components/BarcodeScanInput';

const PAGE_SIZE = 50;
const PRODUCT_FORM_KEY = 'webb_product_form';

const Products = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const prodSaved = loadDraft(PRODUCT_FORM_KEY);
  const [showForm, setShowForm] = useState(prodSaved?.showForm || false);
  const [unitPriceTouched, setUnitPriceTouched] = useState(false);
  const [expandedId, _setExpandedId] = useState(null);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [barcodeModal, setBarcodeModal] = useState(null);
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [formData, setFormData] = useState(prodSaved?.formData || {
    name: '', sku: '', category: '', unit_price: 0, cost_price: 0, description: '', vendor_id: '', vendor_name: '', pwo_taxable: true
  });
  const [vendors, setVendors] = useState([]);
  const [pricingSchemes, setPricingSchemes] = useState([]);
  const [productCategories, setProductCategories] = useState([]);

  // Bulk category assignment state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkCategoryOpen, setBulkCategoryOpen] = useState(false);
  const [bulkCategoryValue, setBulkCategoryValue] = useState('');
  const [bulkShowDropdown, setBulkShowDropdown] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAllOnPage = () => {
    const pageIds = products.map(p => p.id);
    const allSelected = pageIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) pageIds.forEach(id => next.delete(id));
      else pageIds.forEach(id => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const bulkMatchingCategories = bulkCategoryValue
    ? productCategories.filter(c => (c.name || '').toLowerCase().includes(bulkCategoryValue.toLowerCase())).slice(0, 10)
    : productCategories.slice(0, 10);

  const applyBulkCategory = async (categoryName) => {
    const cat = (categoryName || '').trim();
    if (!cat || selectedIds.size === 0) return;
    setBulkSaving(true);
    try {
      const res = await api.request('/api/products/bulk-category', {
        method: 'PUT',
        body: JSON.stringify({ product_ids: Array.from(selectedIds), category: cat }),
      });
      alert(`Updated ${res.updated} product${res.updated === 1 ? '' : 's'} to "${cat}".`);
      setBulkCategoryOpen(false);
      setBulkCategoryValue('');
      setBulkShowDropdown(false);
      clearSelection();
      loadProducts();
      loadCategories();
    } catch (err) {
      alert('Bulk update failed: ' + err.message);
    } finally {
      setBulkSaving(false);
    }
  };

  // Reset to page 1 whenever the search changes
  useEffect(() => { setCurrentPage(1); }, [search]);

  // Reload products whenever search OR pagination changes
  useEffect(() => { loadProducts(); }, [search, currentPage]); // eslint-disable-line
  useEffect(() => { loadVendors(); loadSchemes(); loadCategories(); loadCount(); }, []); // eslint-disable-line
  useEffect(() => { loadCount(); }, [search]); // eslint-disable-line

  // Sync search to URL params so back navigation preserves it
  useEffect(() => {
    const current = searchParams.get('search') || '';
    if (search !== current) {
      if (search) {
        setSearchParams({ search }, { replace: true });
      } else {
        searchParams.delete('search');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [search]); // eslint-disable-line

  const loadVendors = async () => {
    try { const data = await api.getVendors(); setVendors(data); } catch {}
  };

  const loadSchemes = async () => {
    try { const data = await api.request('/api/pricing-schemes'); setPricingSchemes(data); } catch {}
  };

  const loadCategories = async () => {
    try { const data = await api.request('/api/product-categories'); setProductCategories(data); } catch {}
  };

  // Autosave product form
  useAutosave(PRODUCT_FORM_KEY, { formData, showForm });

  const loadCount = async () => {
    try {
      const res = await api.request(`/api/products/count?search=${encodeURIComponent(search || '')}`);
      setTotalCount(res?.total || 0);
    } catch (e) { console.error('Failed to load count:', e); }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const skip = (currentPage - 1) * PAGE_SIZE;
      const data = await api.getProducts({ search, skip, limit: PAGE_SIZE });
      setProducts(data);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewProduct = () => {
    // SKU + barcode are auto-generated by the backend (atomic counter — never collides).
    // Leaving these blank lets the user override if they have a vendor SKU/UPC.
    setFormData({ name: '', sku: '', category: '', unit_price: 0, cost_price: 0, description: '', vendor_id: '', vendor_name: '', pwo_taxable: true, barcode: '', pricing_scheme: 'regular' });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vendor_id) { alert('Vendor is required. Please select a vendor.'); return; }
    if (!formData.pricing_scheme) { alert('Pricing scheme is required. Please select a pricing scheme.'); return; }
    if (!(formData.cost_price > 0)) { alert('Cost price is required. Enter what you pay the vendor for this item.'); return; }
    try {
      await api.createProduct({
        ...formData,
        cost_price: Number(formData.cost_price) || 0,
        unit_price: Number(formData.unit_price) || 0,
      });
      setShowForm(false);
      setUnitPriceTouched(false);
      setFormData({ name: '', sku: '', category: '', unit_price: 0, cost_price: 0, description: '', vendor_id: '', vendor_name: '', pwo_taxable: true, barcode: '', pricing_scheme: 'regular' });
      clearDraft(PRODUCT_FORM_KEY);
      loadProducts();
    } catch (error) {
      alert('Failed to create product: ' + error.message);
    }
  };

  // Regular selling price suggested from cost + the chosen pricing scheme (mirrors the server-side cascade)
  const deriveUnitPrice = (cost, schemeKey) => {
    const c = Number(cost) || 0;
    const scheme = pricingSchemes.find(s => s.key === schemeKey);
    if (!scheme) return c;
    if (scheme.is_cost) return Math.round(c * 100) / 100;
    const markup = Number(scheme.markup_percent) || 0;
    return Math.round(c * (1 + markup / 100) * 100) / 100;
  };

  // Sort the current page client-side; server already returns name-asc by default
  const sorted = [...products].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }
    return sortDir === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
  });

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const paginatedProducts = sorted; // server already paginated

  const toggleSort = (field) => {
    if (sortField === field) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortField(field); setSortDir('asc'); }
    setCurrentPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp className="inline h-3 w-3 ml-0.5" /> : <ChevronDown className="inline h-3 w-3 ml-0.5" />;
  };

  const generateBarcode = async (sku, productName, productDescription) => {
    if (!sku) { alert('No SKU available for this product'); return; }
    setBarcodeLoading(true);
    try {
      const result = await api.generateBarcode(sku, 'code128');
      result.product_name = productName || '';
      result.product_description = productDescription || '';
      setBarcodeModal(result);
    } catch (err) {
      alert('Failed to generate barcode: ' + err.message);
    } finally {
      setBarcodeLoading(false);
    }
  };

  const handleBarcodeDownload = (dataUrl, name) => {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `barcode-${name}.png`;
    link.click();
  };

  const _handleBarcodePrint = (dataUrl, sku, productName, printerType = 'dymo', _svg = '') => {
    const escapedName = (productName || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const w = window.open('', '_blank', 'width=600,height=400');
    if (!w) { alert('Please allow popups for this site.'); return; }

    if (printerType === 'dymo') {
      w.document.write(`<!DOCTYPE html><html><head><title>Barcode</title>
<style>
@page { size: 3.5in 1.125in; margin: 0; }
@media print { body { margin: 0 !important; padding: 0.06in 0.1in !important; border: none !important; } }
* { box-sizing: border-box; margin: 0; padding: 0; }
body { width: 3.5in; height: 1.125in; margin: 20px auto; padding: 0.06in 0.1in; display: flex; flex-direction: column; justify-content: center; font-family: Arial, Helvetica, sans-serif; overflow: hidden; border: 1px dashed #ccc; }
.pname { font-size: 8pt; font-weight: bold; line-height: 1.15; max-height: 0.28in; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-bottom: 2px; }
.bc { display: flex; align-items: center; justify-content: center; }
.bc img { max-width: 3.0in; height: 0.52in; }
.sku { font-family: 'Courier New', monospace; font-size: 7.5pt; text-align: center; margin-top: 2px; letter-spacing: 0.03em; }
</style></head><body>
<div class="pname">${escapedName}</div>
<div class="bc"><img id="bc-img" src="${dataUrl}" /></div>
<div class="sku">${sku}</div>
<script>
var img = document.getElementById('bc-img');
function doPrint() { setTimeout(function() { window.print(); }, 200); }
if (img.complete && img.naturalWidth > 0) { doPrint(); }
else { img.onload = doPrint; img.onerror = doPrint; }
</script>
</body></html>`);
    } else {
      w.document.write(`<!DOCTYPE html><html><head><title>Barcode</title>
<style>
body { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 100vh; margin: 0; font-family: Arial, sans-serif; }
.label { width: 3.5in; padding: 0.2in; text-align: center; border: 1px dashed #ccc; }
.pname { font-size: 10pt; font-weight: bold; margin-bottom: 6px; }
.bc img { max-width: 3in; height: 0.8in; }
.sku { font-family: 'Courier New', monospace; font-size: 9pt; margin-top: 4px; letter-spacing: 0.05em; }
@media print { .label { border: none; } }
</style></head><body>
<div class="label">
<div class="pname">${escapedName}</div>
<div class="bc"><img id="bc-img" src="${dataUrl}" /></div>
<div class="sku">${sku}</div>
</div>
<script>
var img = document.getElementById('bc-img');
function doPrint() { setTimeout(function() { window.print(); }, 200); }
if (img.complete && img.naturalWidth > 0) { doPrint(); }
else { img.onload = doPrint; img.onerror = doPrint; }
</script>
</body></html>`);
    }
    w.document.close();
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Products</h2>
          <p className="text-muted-foreground">{sorted.length} products</p>
        </div>
        <button onClick={handleNewProduct}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90" data-testid="add-product-btn">
          <Plus className="h-4 w-4" /> Add Product
        </button>
      </div>

      {showForm && (
        <div className="bg-card p-6 rounded-lg border border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Create New Product</h3>
            <button onClick={() => setShowForm(false)} className="p-1 hover:bg-muted rounded"><X className="h-5 w-5" /></button>
          </div>
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Product Name *</label>
              <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-input rounded-md" data-testid="product-name-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">SKU <span className="text-xs text-muted-foreground">(leave blank to auto-generate)</span></label>
              <input type="text" value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                placeholder="Auto-generated as WS-NNNNNN on save"
                className="w-full px-3 py-2 border border-input rounded-md" data-testid="product-sku-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Category *</label>
              <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border border-input rounded-md" data-testid="product-category-select">
                <option value="">-- Select Category --</option>
                {productCategories.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Vendor *</label>
              <select value={formData.vendor_id} onChange={(e) => {
                const v = vendors.find(v => v.id === e.target.value);
                setFormData({ ...formData, vendor_id: e.target.value, vendor_name: v?.name || '' });
              }} className="w-full px-3 py-2 border border-input rounded-md" data-testid="product-vendor-select">
                <option value="">-- Select Vendor --</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Cost Price * <span className="font-normal text-muted-foreground">(what you pay the vendor)</span></label>
              <input type="number" step="0.01" min="0" required value={Number.isFinite(formData.cost_price) ? formData.cost_price : ''}
                onChange={(e) => {
                  const cost = parseFloat(e.target.value);
                  const next = { ...formData, cost_price: Number.isFinite(cost) ? cost : 0 };
                  if (!unitPriceTouched) next.unit_price = deriveUnitPrice(next.cost_price, next.pricing_scheme);
                  setFormData(next);
                }}
                className="w-full px-3 py-2 border border-input rounded-md" data-testid="product-cost-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Unit Price * <span className="font-normal text-muted-foreground">(regular selling price)</span></label>
              <input type="number" step="0.01" min="0" required value={Number.isFinite(formData.unit_price) ? formData.unit_price : ''}
                onChange={(e) => { setUnitPriceTouched(true); const v = parseFloat(e.target.value); setFormData({ ...formData, unit_price: Number.isFinite(v) ? v : 0 }); }}
                className="w-full px-3 py-2 border border-input rounded-md" data-testid="product-unit-price-input" />
              {!unitPriceTouched && formData.cost_price > 0 && (
                <p className="text-xs text-muted-foreground mt-1">Auto-calculated from cost using the selected pricing scheme. Type to override.</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Pricing Scheme *</label>
              <select value={formData.pricing_scheme || ''}
                onChange={(e) => {
                  const next = { ...formData, pricing_scheme: e.target.value };
                  if (!unitPriceTouched) next.unit_price = deriveUnitPrice(next.cost_price, next.pricing_scheme);
                  setFormData(next);
                }}
                className="w-full px-3 py-2 border border-input rounded-md" data-testid="product-pricing-scheme">
                <option value="">-- Select Pricing Scheme --</option>
                {pricingSchemes.map(s => (
                  <option key={s.id} value={s.key}>{s.name}{s.is_cost ? ' (at cost)' : s.markup_percent ? ` (${s.markup_percent}% markup)` : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">PWO Sales Tax</label>
              <select value={formData.pwo_taxable ? 'taxable' : 'non_taxable'}
                onChange={(e) => setFormData({ ...formData, pwo_taxable: e.target.value === 'taxable' })}
                className="w-full px-3 py-2 border border-input rounded-md" data-testid="product-pwo-tax">
                <option value="taxable">Taxable to PWO</option>
                <option value="non_taxable">Non-Taxable to PWO</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">Non-taxable only applies to Patrick Webb Oil, LLC. All other customers are always charged sales tax.</p>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="2" className="w-full px-3 py-2 border border-input rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Barcode <span className="text-xs text-muted-foreground">(leave blank to auto-generate)</span></label>
              <input type="text" value={formData.barcode || ''} onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                placeholder="Defaults to the SKU on save"
                className="w-full px-3 py-2 border border-input rounded-md" data-testid="product-barcode-input" />
            </div>
            <div className="col-span-2 flex gap-2 pt-2">
              <button type="submit" className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-md hover:bg-primary/90 font-medium" data-testid="save-product-btn">
                Save Product
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2.5 border border-border rounded-md hover:bg-muted">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input type="text" placeholder="Search by name, SKU, or description..." value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            className="w-full pl-9 pr-4 py-2 border border-input rounded-md" data-testid="product-search" />
        </div>
        <div className="flex-1 min-w-[280px]">
          <BarcodeScanInput
            onScan={(product) => { if (product?.product_id) navigate(`/products/${product.product_id}`); }}
            placeholder="Scan barcode → open product"
            autoFocus={false}
          />
        </div>
        {/* Bulk-assign category action — visible once the user has ticked at least one row */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200" data-testid="bulk-action-bar">
            <span className="text-sm font-medium text-amber-900">{selectedIds.size} selected</span>
            {!bulkCategoryOpen ? (
              <>
                <button
                  type="button"
                  onClick={() => { setBulkCategoryOpen(true); setBulkShowDropdown(true); }}
                  className="px-3 py-1.5 text-sm rounded-md bg-amber-600 text-white hover:bg-amber-700"
                  data-testid="bulk-assign-category-btn"
                >
                  Assign to Category
                </button>
                <button type="button" onClick={clearSelection}
                  className="px-2 py-1 text-xs rounded-md text-amber-700 hover:bg-amber-100" data-testid="bulk-clear-btn">
                  Clear
                </button>
              </>
            ) : (
              <div className="relative">
                <input
                  autoFocus
                  type="text"
                  value={bulkCategoryValue}
                  onChange={(e) => { setBulkCategoryValue(e.target.value); setBulkShowDropdown(true); }}
                  onFocus={() => setBulkShowDropdown(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); applyBulkCategory(bulkCategoryValue); }
                    if (e.key === 'Escape') { setBulkCategoryOpen(false); setBulkShowDropdown(false); }
                  }}
                  placeholder="Type or choose a category..."
                  className="px-2 py-1.5 border border-input rounded-md text-sm w-56"
                  disabled={bulkSaving}
                  data-testid="bulk-category-input"
                />
                {bulkShowDropdown && bulkMatchingCategories.length > 0 && (
                  <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-md shadow-lg z-50 max-h-56 overflow-y-auto w-56" data-testid="bulk-category-dropdown">
                    {bulkMatchingCategories.map((c) => (
                      <button
                        key={c.id || c.name}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => applyBulkCategory(c.name)}
                        className="w-full text-left px-3 py-1.5 hover:bg-muted text-sm"
                        data-testid={`bulk-category-option-${c.name}`}
                      >
                        {c.name}
                      </button>
                    ))}
                  </div>
                )}
                <button type="button" onClick={() => applyBulkCategory(bulkCategoryValue)}
                  disabled={!bulkCategoryValue.trim() || bulkSaving}
                  className="ml-2 px-3 py-1.5 text-sm rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                  data-testid="bulk-category-apply-btn">
                  {bulkSaving ? 'Saving...' : 'Apply'}
                </button>
                <button type="button" onClick={() => { setBulkCategoryOpen(false); setBulkShowDropdown(false); }}
                  className="ml-1 px-2 py-1 text-xs text-amber-700 hover:bg-amber-100 rounded-md"
                  disabled={bulkSaving} data-testid="bulk-category-cancel-btn">
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="w-10 px-2 text-center">
                  <input
                    type="checkbox"
                    checked={products.length > 0 && products.every(p => selectedIds.has(p.id))}
                    onChange={toggleSelectAllOnPage}
                    className="accent-primary cursor-pointer"
                    data-testid="select-all-products"
                    aria-label="Select all products on this page"
                  />
                </th>
                <th className="w-8 px-2"></th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('name')}>
                  Product <SortIcon field="name" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('sku')}>
                  SKU <SortIcon field="sku" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('category')}>
                  Category <SortIcon field="category" />
                </th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('normal_price')}>
                  Regular Price <SortIcon field="normal_price" />
                </th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('pwo_price')}>
                  PW OIL Price <SortIcon field="pwo_price" />
                </th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('cost_price')}>
                  Cost <SortIcon field="cost_price" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('barcode')}>
                  Barcode <SortIcon field="barcode" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Vendor</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('quantity_on_hand')}>
                  Qty On Hand <SortIcon field="quantity_on_hand" />
                </th>
                <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Tax</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr><td colSpan="12" className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
              ) : paginatedProducts.length === 0 ? (
                <tr><td colSpan="12" className="px-4 py-8 text-center text-muted-foreground">
                  <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />No products found
                </td></tr>
              ) : (
                paginatedProducts.map((p) => (
                  <React.Fragment key={p.id}>
                    <tr className="hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/products/${p.id}`)} data-testid={`product-row-${p.id}`}>
                      <td className="px-2 py-3 text-center" onClick={(e) => { e.stopPropagation(); toggleSelect(p.id); }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(p.id)}
                          onChange={() => toggleSelect(p.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="accent-primary cursor-pointer"
                          data-testid={`select-product-${p.id}`}
                        />
                      </td>
                      <td className="px-2 py-3 text-center">
                        {expandedId === p.id ? <ChevronUp className="h-4 w-4 text-muted-foreground inline" /> : <ChevronDown className="h-4 w-4 text-muted-foreground inline" />}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium">{p.name}</p>
                        {p.description && <p className="text-xs text-muted-foreground line-clamp-1">{p.description}</p>}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono">{p.sku || '\u2014'}</td>
                      <td className="px-4 py-3 text-sm">{p.category || '\u2014'}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium tabular-nums">${(p.normal_price || p.unit_price || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-right tabular-nums text-amber-700 font-medium" data-testid={`pwo-price-${p.sku}`}>${(p.pwo_price ?? p.cost_price ?? p.unit_price ?? 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-right tabular-nums text-muted-foreground">${(p.cost_price || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-center">
                        {p.barcode || p.sku ? (
                          <button onClick={(e) => { e.stopPropagation(); generateBarcode(p.sku || p.barcode, p.name, p.description); }}
                            disabled={barcodeLoading}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-mono text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
                            title="Click to generate & print barcode"
                            data-testid={`barcode-btn-${p.id}`}>
                            <Barcode className="h-4 w-4" />
                            {p.barcode || p.sku}
                          </button>
                        ) : (
                          <span className="text-muted-foreground">{'\u2014'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{p.last_vendor || '\u2014'}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium tabular-nums">
                        <span className={`${(p.quantity_on_hand || 0) === 0 ? 'text-red-500' : (p.quantity_on_hand || 0) <= 10 ? 'text-amber-600' : 'text-green-700'}`}>
                          {p.quantity_on_hand || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={(e) => {
                            e.stopPropagation();
                            const newVal = p.is_taxable === false;
                            api.request(`/api/products/${p.id}/taxable`, { method: 'PUT', body: JSON.stringify({ is_taxable: newVal }) })
                              .then(() => { setProducts(prev => prev.map(pr => pr.id === p.id ? { ...pr, is_taxable: newVal } : pr)); })
                              .catch(err => alert('Failed: ' + err.message));
                          }}
                          className={`px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${p.is_taxable !== false ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          data-testid={`tax-toggle-${p.id}`}>
                          {p.is_taxable !== false ? 'Yes' : 'No'}
                        </button>
                      </td>
                    </tr>
                    {expandedId === p.id && (
                      <tr data-testid={`product-detail-${p.id}`}>
                        <td colSpan="12" className="px-6 py-4 bg-muted/20">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            <div>
                              <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Pricing</h4>
                              <div className="space-y-1 text-sm">
                                <div><span className="text-muted-foreground">Default Price:</span> ${(p.unit_price || 0).toFixed(2)}</div>
                                <div><span className="text-muted-foreground">Normal Price:</span> {p.normal_price ? `$${p.normal_price.toFixed(2)}` : '\u2014'}</div>
                                <div><span className="text-muted-foreground">Wholesale:</span> {p.wholesale_price ? `$${p.wholesale_price.toFixed(2)}` : '\u2014'}</div>
                                <div><span className="text-muted-foreground">Cost:</span> ${(p.cost_price || 0).toFixed(2)}</div>
                                <div><span className="text-muted-foreground">Markup:</span> {p.markup || '\u2014'}</div>
                                {p.is_tax_inclusive && <div className="text-green-600">Tax Inclusive</div>}
                                {p.special_tax_rate > 0 && <div><span className="text-muted-foreground">Special Tax:</span> {p.special_tax_rate}%</div>}
                              </div>
                            </div>
                            <div>
                              <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Vendor</h4>
                              <div className="space-y-1 text-sm">
                                <div><span className="text-muted-foreground">Last Vendor:</span> {p.last_vendor || '\u2014'}</div>
                                <div><span className="text-muted-foreground">Vendor Code:</span> {p.vendor_product_code || '\u2014'}</div>
                                <div><span className="text-muted-foreground">Vendor Price:</span> {p.vendor_price ? `$${p.vendor_price.toFixed(2)}` : '\u2014'}</div>
                              </div>
                            </div>
                            <div>
                              <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Details</h4>
                              <div className="space-y-1 text-sm">
                                <div><span className="text-muted-foreground">Barcode:</span> {p.barcode || '\u2014'}</div>
                                <div><span className="text-muted-foreground">UOM:</span> {p.uom || '\u2014'}</div>
                                {p.sales_uom && <div><span className="text-muted-foreground">Sales UOM:</span> {p.sales_uom}</div>}
                                {p.purchasing_uom && <div><span className="text-muted-foreground">Purchasing UOM:</span> {p.purchasing_uom}</div>}
                                <div><span className="text-muted-foreground">Active:</span> {p.is_active ? 'Yes' : 'No'}</div>
                                {p.hs_code && <div><span className="text-muted-foreground">HS Code:</span> {p.hs_code}</div>}
                                {p.country_of_origin && <div><span className="text-muted-foreground">Origin:</span> {p.country_of_origin}</div>}
                              </div>
                            </div>
                            <div>
                              <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Dimensions</h4>
                              <div className="space-y-1 text-sm">
                                {p.product_length && <div><span className="text-muted-foreground">L:</span> {p.product_length}</div>}
                                {p.product_width && <div><span className="text-muted-foreground">W:</span> {p.product_width}</div>}
                                {p.product_height && <div><span className="text-muted-foreground">H:</span> {p.product_height}</div>}
                                {p.product_weight && <div><span className="text-muted-foreground">Weight:</span> {p.product_weight}</div>}
                                {p.remarks && <div className="mt-2"><span className="text-muted-foreground">Remarks:</span> {p.remarks}</div>}
                              </div>
                            </div>
                          </div>
                          {p.raw_data && Object.keys(p.raw_data).length > 0 && (
                            <details className="mt-4 pt-3 border-t border-border">
                              <summary className="text-xs font-semibold uppercase text-muted-foreground cursor-pointer flex items-center gap-1.5">
                                <Database className="h-3.5 w-3.5" /> All Import Data ({Object.entries(p.raw_data).filter(([,v]) => v && String(v).trim()).length} fields)
                              </summary>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 mt-2">
                                {Object.entries(p.raw_data).filter(([,v]) => v && String(v).trim()).map(([k,v]) => (
                                  <div key={k} className="bg-muted/40 rounded px-2 py-1">
                                    <span className="text-[10px] text-muted-foreground font-mono block">{k}</span>
                                    <span className="text-xs">{String(v)}</span>
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/30" data-testid="products-pagination">
            <p className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount} products
            </p>
            <div className="flex items-center gap-1">
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
                className="px-2.5 py-1.5 text-xs border border-input rounded-md hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed" data-testid="page-first">First</button>
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                className="p-1.5 border border-input rounded-md hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed" data-testid="page-prev">
                <ChevronLeft className="h-4 w-4" />
              </button>
              {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                let page;
                if (totalPages <= 7) {
                  page = i + 1;
                } else if (currentPage <= 4) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 3) {
                  page = totalPages - 6 + i;
                } else {
                  page = currentPage - 3 + i;
                }
                return (
                  <button key={page} onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                      currentPage === page ? 'bg-primary text-white border-primary' : 'border-input hover:bg-muted'
                    }`} data-testid={`page-${page}`}>{page}</button>
                );
              })}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                className="p-1.5 border border-input rounded-md hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed" data-testid="page-next">
                <ChevronRight className="h-4 w-4" />
              </button>
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}
                className="px-2.5 py-1.5 text-xs border border-input rounded-md hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed" data-testid="page-last">Last</button>
            </div>
          </div>
        )}
      </div>

      {/* Barcode Modal */}
      {barcodeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="barcode-modal">
          <div className="bg-card rounded-lg border border-border p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Barcode: {barcodeModal.sku}</h3>
              <button onClick={() => setBarcodeModal(null)} className="p-1 hover:bg-muted rounded"><X className="h-5 w-5" /></button>
            </div>
            {barcodeModal.product_name && (
              <p className="text-sm font-medium text-center mb-2" data-testid="barcode-product-name">{barcodeModal.product_name}</p>
            )}
            <div className="bg-white p-6 rounded-lg border text-center">
              <img src={barcodeModal.image_data_url} alt={`Barcode ${barcodeModal.sku}`} className="max-w-full mx-auto" data-testid="barcode-image" />
            </div>
            <p className="text-center text-xs text-muted-foreground mt-2">Dymo 30252 (1-1/8" x 3-1/2") &mdash; Product Name + Barcode + SKU</p>
            <div className="flex justify-center gap-3 mt-4">
              <button onClick={() => handleBarcodeDownload(barcodeModal.image_data_url, barcodeModal.sku)}
                className="flex items-center gap-2 px-4 py-2 border border-input rounded-md hover:bg-muted" data-testid="barcode-download">
                <Download className="h-4 w-4" /> Download
              </button>
              <button onClick={() => {
                  const params = new URLSearchParams({ sku: barcodeModal.sku, name: barcodeModal.product_name || '', description: barcodeModal.product_description || '' });
                  setBarcodeModal(null);
                  navigate(`/print-barcode?${params.toString()}`);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90" data-testid="barcode-print-dymo">
                <Printer className="h-4 w-4" /> Print Label
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
